import { Component, ChangeDetectionStrategy, EventEmitter, Input, Output, ViewChild, OnInit, Renderer2, OnDestroy, ChangeDetectorRef, ElementRef } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { OAuthService } from 'angular-oauth2-oidc';
import { isFunction } from 'util';
import * as moment from 'moment';
import 'moment-timezone';
import { Store } from '@ngrx/store';

import { LightboxInterface, LightboxComponent } from '@gosuite/lightbox';
import { DropdownItem } from '@gosuite/selects';

import * as appSelectors from '../../state-management/reducers';
import { Group } from '../../../xena/core/shared/models/groups/groups.model';
import { PrrlStoreFileModel } from './prrl-store-file.model';
import { PrrlInfoModel } from '../../../xena/core/shared/models/validation-response.model';
import { PrrlFormButlerTokenService } from './prrl-form-butler-token.service';


@Component({
	selector: 'prrl-form-butler-dialog',
	templateUrl: './prrl-form-butler-dialog.component.html',
	styleUrls: ['./prrl-form-butler-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class PrrlFormButlerDialogComponent implements LightboxInterface, OnInit, OnDestroy {
	@ViewChild('lightboxInstance') public lightboxInstance: LightboxComponent;

	@ViewChild('frame') public frame: ElementRef;

	@Output() public onOpen: EventEmitter<any> = new EventEmitter();
	@Output() public onClose: EventEmitter<any> = new EventEmitter();
	@Output() public onClickCloseButton: EventEmitter<any> = new EventEmitter();
	@Output() public onClickBackground: EventEmitter<any> = new EventEmitter();
	@Output() public onEscapeKeyPress: EventEmitter<any> = new EventEmitter();

	@Input() public showCloseButton: boolean = true;
	@Input() public showHeader: boolean = false;
	@Input() public showFooter: boolean = false;
	@Input() public showBackground: boolean = true;
	@Input() public backgroundColor: string = null;
	@Input() public boxColor: string = null;
	@Input() public showBoxShadow: boolean = true;
	@Input() public boxWidth: string = '980px';
	@Input() public boxHeight: string = '640px';
	@Input() public closeByCloseButtonClick: boolean = true;
	@Input() public closeByBackgroundClick: boolean = true;
	@Input() public closeOnEscapeKey: boolean = true;
	@Input() public isFancy: boolean = false;
	@Input() public closeButtonFixed: boolean = false;

	public isOpen: boolean = false;
	public showLoader: boolean = false;
	public frameUrl: SafeResourceUrl = null;
	public urls: DropdownItem[] = [];
	public selectedUrl: DropdownItem = null;
	public signCode: number = null;
	public uuid: string = '';
	public offer: Group = null;
	public prrlInfo: PrrlInfoModel = null;

	public set operator(top: string) {
		this._operator = top;
		this.frameUrl = this._getFrameUrl();
	}

	public get operator(): string {
		return this._operator;
	}

	private _operator: string = '';
	private windowMsgListener: () => void;

	public constructor(
		private _renderer2: Renderer2,
		private _cd: ChangeDetectorRef,
		private _oauthService: OAuthService,
		private _store: Store<appSelectors.State>,
		private _sanitizer: DomSanitizer,
		private _tokenService: PrrlFormButlerTokenService,
	) {}

	public ngOnInit() {
		this.frameUrl = this._getFrameUrl();

		this.windowMsgListener = this._renderer2.listen('window', 'message', ({data}: MessageEvent) => {
			switch (data.action) {
				case 'prrl:view-document':
					this._onViewDocument();
					break;

				case 'prrl:stored-document':
					this._onStoredDocument(data.payload);
					break;
			}
		});
	}

	public ngOnDestroy() {
		if (isFunction(this.windowMsgListener)) {
			this.windowMsgListener();
		}
	}

	public open(): void {
		this.isOpen = true;
		this.lightboxInstance.open();
	}

	public close(): void {
		this.isOpen = false;
		this.lightboxInstance.close();
	}

	public handleLightboxOpen(): void {
		this.onOpen.emit();
	}

	public handleLightboxClose(): void {
		setTimeout(() => { this.onClose.emit(); }, 250); // if < 250 sometimes error cannot read 'send' of undefined occurs
	}

	public handleClickCloseButton(): void {
		this.onClickCloseButton.emit();

		if (this.closeByCloseButtonClick) {
			this.close();
		}
	}

	public handleClickBackground(): void {
		this.onClickBackground.emit();

		if (this.closeByBackgroundClick) {
			this.close();
		}
	}

	public handleEscapeKeyPress(): void {
		this.onEscapeKeyPress.emit();

		if (this.closeOnEscapeKey) {
			this.close();
		}
	}

	public storeDocument(): void {
		if (this.showLoader || this.signCode) {
			return;
		}

		this.showLoader = true;

		const payload = this.getDocumentData();

		this.frame.nativeElement.contentWindow.postMessage({
			action: 'upload-to-storage',
			payload,
		}, '*');
	}

	public storeDocumentAndSign(): void {
		if (this.showLoader || this.signCode) {
			return;
		}

		this.showLoader = true;

		const payload = this.getDocumentData();
		const claims = this._oauthService.getIdentityClaims();
		payload.sign = true;
		payload.user = {
			username: claims['user'].name,
			first_name: claims['user'].first_name,
			last_name: claims['user'].last_name,
			email: claims['user'].email,
		};

		this.frame.nativeElement.contentWindow.postMessage({
			action: 'upload-to-storage',
			payload,
		}, '*');
	}

	private getDocumentData(): { [s: string]: any} {
		const claims = this._oauthService.getIdentityClaims();
		let selectedAgency: number = -1;

		this._store.select(appSelectors.getSelectedAgency).take(1).subscribe(agency => selectedAgency = agency.agentId).unsubscribe();

		const payload = {
			agent: selectedAgency,
			user: claims['sub'],
			application: 'XENA',
		};

		if (this.offer) {
			payload['operator'] = this.offer.tourOperator;
			payload['travel_date'] = `${moment(this.offer.startDate).format('DD.MM.YYYY')} - ${moment(this.offer.endDate).format('DD.MM.YYYY')}`;
		}

		return payload;
	}

	private _onViewDocument() {
		this.showFooter = true;
		this._cd.detectChanges();
	}

	private _onStoredDocument({pin, uuid, urls}: PrrlStoreFileModel) {
		this.showFooter = false;
		this.showLoader = false;

		if (!pin) {
			this.close();
			return;
		}

		this.boxHeight = '300px';
		this.signCode = pin;
		this.uuid = uuid;
		const dropdownItems = urls.map(url => ({value: url, rawValue: url, displayName: url, displayNameShort: url}));
		const genericUrl = 'https://kundenportal.reisen/pin';
		dropdownItems.unshift({value: genericUrl, rawValue: genericUrl, displayName: genericUrl, displayNameShort: genericUrl});
		this.urls = dropdownItems;
		this.selectedUrl = this.urls[0];
		this._cd.detectChanges();
	}

	private _getFrameUrl(): SafeResourceUrl {
		let selectedAgency: number = -1;
		let prrlDataHash = '';
		this._store.select(appSelectors.getSelectedAgency).take(1).subscribe(agency => selectedAgency = agency.agentId).unsubscribe();

		if (this.prrlInfo && this.prrlInfo.legalIdentity) {
			prrlDataHash = encodeURIComponent(btoa(JSON.stringify({
				legalIdentity: this.prrlInfo.legalIdentity,
				insolvencyInsurance: this.prrlInfo.insolvencyInsurance,
			})));
		}

		if (this.prrlInfo && this.prrlInfo.packagedTour) {
			return this._sanitizer.bypassSecurityTrustResourceUrl(`https://prrl.go-suite.com/${selectedAgency}/package/${this.operator}${prrlDataHash ? ('/' + prrlDataHash) : ''}?token=${this._tokenService.getToken()}`);
		} else if (this.operator) {
			return this._sanitizer.bypassSecurityTrustResourceUrl(`https://prrl.go-suite.com/${selectedAgency}/${this.operator}?token=${this._tokenService.getToken()}`);
		}

		return this._sanitizer.bypassSecurityTrustResourceUrl(`https://prrl.go-suite.com/${selectedAgency}?token=${this._tokenService.getToken()}`);
	}
}



// WEBPACK FOOTER //
// ./src/app/shared/components/prrl-form-butler-dialog/prrl-form-butler-dialog.component.ts